import { z } from 'zod';

/**
 * Validadores de campos reutilizables para los formularios (react-hook-form + zod).
 * Fuente única de las reglas (formato + longitud + mensajes en es-MX). Importar estos
 * campos en los schemas en vez de re-declarar `z.string()` suelto en cada form.
 */

/** Normaliza un teléfono a solo dígitos, conservando un `+` inicial (E.164). */
export function normalizePhone(value: string): string {
    const trimmed = value.trim();
    const hasPlus = trimmed.startsWith('+');
    const digits = trimmed.replace(/\D/g, '');
    return hasPlus ? `+${digits}` : digits;
}

/**
 * ¿Teléfono válido? Lenient a propósito para NO bloquear la edición de usuarios con teléfonos
 * legacy: acepta 7–15 dígitos, con `+` opcional (E.164). Ignora espacios, guiones, paréntesis y
 * puntos. Rechaza lo obviamente inválido (letras, muy corto). El ideal en México son 10 dígitos.
 */
export function isValidPhone(value: string): boolean {
    return /^\+?\d{7,15}$/.test(normalizePhone(value));
}

const PHONE_MESSAGE = 'Teléfono inválido: usa solo dígitos (10 en México), o +código de país.';

/** Email requerido y con formato válido. */
export const emailField = z
    .string()
    .trim()
    .min(1, 'El email es requerido')
    .email('Email inválido')
    .max(254, 'El email es demasiado largo');

/** Nombre requerido (1–80 caracteres). */
export const requiredNameField = z
    .string()
    .trim()
    .min(1, 'El nombre es requerido')
    .max(80, 'Máximo 80 caracteres');

/** Nombre opcional (hasta 80 caracteres) — para perfil propio. */
export const optionalNameField = z.string().trim().max(80, 'Máximo 80 caracteres').optional();

/** Teléfono opcional; si se ingresa, debe ser válido. Vacío se acepta. */
export const optionalPhoneField = z
    .string()
    .trim()
    .max(20, 'Teléfono demasiado largo')
    .refine((v) => v === '' || isValidPhone(v), PHONE_MESSAGE)
    .optional();

/** Un string requerido genérico con límite de longitud y mensajes en español. */
export function requiredString(label = 'Este campo', max = 120): z.ZodString {
    return z
        .string()
        .trim()
        .min(1, `${label} es requerido`)
        .max(max, `Máximo ${String(max)} caracteres`);
}
